import { useMediaQuery } from 'react-responsive';
import { useState, useEffect } from 'react';
import { TResponsiveType, TResponsiveObject, TUseResponsiveReturnObject, TUseResponsiveCheckPrefix } from '@features/responsive/typescript';
import { firstUppercase } from '@utils/misc';
import theme from '@styles/theme';
const useResponsive = (): TUseResponsiveReturnObject => {
  const viewBreakpoints: number[] = [];
  theme.breakpoints.forEach(breakpoint => viewBreakpoints.push(parseInt(breakpoint.replace('px', ''), 10)));
  const responsiveObject: TResponsiveObject = {
    mobile: useMediaQuery({
      maxWidth: viewBreakpoints[0] - 1
    }),
    mobilelg: useMediaQuery({
      minWidth: viewBreakpoints[0],
      maxWidth: viewBreakpoints[1] - 1
    }),
    tablet: useMediaQuery({
      minWidth: viewBreakpoints[1],
      maxWidth: viewBreakpoints[2] - 1
    }),
    tabletlg: useMediaQuery({
      minWidth: viewBreakpoints[2],
      maxWidth: viewBreakpoints[3] - 1
    }),
    desktop: useMediaQuery({
      minWidth: viewBreakpoints[3],
      maxWidth: viewBreakpoints[4] - 1
    }),
    widescreen: useMediaQuery({
      minWidth: viewBreakpoints[4],
      maxWidth: viewBreakpoints[5] - 1
    }),
    fullhd: useMediaQuery({
      minWidth: viewBreakpoints[5],
      maxWidth: viewBreakpoints[6] - 1
    }),
    ultrahd: useMediaQuery({
      minWidth: viewBreakpoints[6]
    })
  };
  const {
    mobile,
    mobilelg,
    tablet,
    tabletlg,
    desktop,
    widescreen,
    fullhd,
    ultrahd
  } = responsiveObject;
  const keys = Object.keys(responsiveObject);
  const getKeyName = (prefix: TUseResponsiveCheckPrefix, name: TResponsiveType) => prefix + firstUppercase(name);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('mobile' as TResponsiveType);
  const withObjectAssign = (object: Record<string, string | boolean>, [key, value]: (string | boolean)[]) => Object.assign(object, {
    [key as string]: value
  });
  const createChecks = () => keys.map((key: string) => {
    const keyValue = key as TResponsiveType;
    return [getKeyName('is', keyValue), responsiveObject[keyValue]];
  }).reduce(withObjectAssign, {});
  const createCurrentWiderThan = () => keys.map((key: string, index: number) => {
    const keyValue = key as TResponsiveType;
    const currentIndex = keys.indexOf(currentBreakpoint as string);
    return [getKeyName('isWiderThan', keyValue), currentIndex >= index];
  }).reduce(withObjectAssign, {});
  const createCurrentNarrowerThan = () => keys.map((key: string, index: number) => {
    const keyValue = key as TResponsiveType;
    const currentIndex = keys.indexOf(currentBreakpoint as string);
    return [getKeyName('isNarrowerThan', keyValue), currentIndex <= index];
  }).reduce(withObjectAssign, {});
  const [checks, setChecks] = useState(createChecks());
  const [currentWiderThan, setCurrentWiderThan] = useState(createCurrentWiderThan());
  const [currentNarrowerThan, setCurrentNarrowerThan] = useState(createCurrentNarrowerThan());
  const setInterface = () => {
    setChecks(createChecks());
    setCurrentWiderThan(createCurrentWiderThan());
    setCurrentNarrowerThan(createCurrentNarrowerThan());
  };
  useEffect(() => {
    const isBreakpointSet = Object.keys(responsiveObject).some(key => responsiveObject[key]);
    if (isBreakpointSet) {
      const newBreakpoint = Object.keys(responsiveObject).find((key: string) => responsiveObject[key as TResponsiveType]) as TResponsiveType;
      if (newBreakpoint && newBreakpoint !== currentBreakpoint) {
        setCurrentBreakpoint(newBreakpoint);
      }
    }
  }, [mobile, mobilelg, tablet, tabletlg, desktop, widescreen, fullhd, ultrahd, currentBreakpoint]);
  useEffect(() => {
    setInterface();
  }, [currentBreakpoint]);
  if (typeof window === 'undefined') {
    return {
      isMobile: true,
      currentBreakpoint: 'mobile',
      isWiderThanMobile: false,
      isWiderThanMobilelg: false,
      isWiderThanTablet: false,
      isWiderThanTabletlg: false,
      isWiderThanDesktop: false,
      isWiderThanWidescreen: false,
      isWiderThanFullhd: false,
      isWiderThanUltrahd: false,
      isNarrowerThanMobile: false,
      isNarrowerThanMobilelg: true,
      isNarrowerThanTablet: true,
      isNarrowerThanTabletlg: true,
      isNarrowerThanDesktop: true,
      isNarrowerThanWidescreen: true,
      isNarrowerThanFullhd: true,
      isNarrowerThanUltrahd: true
    };
  }
  return {
    ...checks,
    ...currentWiderThan,
    ...currentNarrowerThan,
    currentBreakpoint
  };
};
export default useResponsive;